import { TranslationKey } from '@Localization';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { useInjection } from 'inversify-react';
import qs from 'qs';
import { ReactElement, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { TranslateServerErrorCodes } from '../../../helpers/serverErrorsTranslator.helper';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { currentTimeZone } from '../../../models/timeZones/timeZones.model';
import { IWorkspacesStore } from '../../workspaces/workspaces.store';
import { useAuthService } from '../auth.service';
import { IAuthStore } from '../auth.store';
import { RegistrationForm } from '../components/RegistrationForm';
import { ResolutionWarningCard } from '../components/ResolutionWarningCard';

export const RegistrationContainer = (): ReactElement => {
  const authService = useAuthService();
  const authStore = useInjection(IAuthStore.$);
  const workspaceStore = useInjection(IWorkspacesStore.$);
  const config = useConfiguration();

  const { withLoaderAsync } = useLoader();
  const location = useLocation();
  const history = useHistory();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [emailStatus, setEmailStatus] = useState(InputStatus.empty());
  const [passwordStatus, setPasswordStatus] = useState(InputStatus.empty());
  const [externalStatus, setExternalStatus] = useState(InputStatus.empty());
  const [isRegistrationPending, setIsRegistrationPending] = useState(false);

  useEffect(() => {
    presetUrlData();
  }, []);

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    setEmailStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
  };

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    setExternalStatus(InputStatus.empty());
  };

  const handleCompanyChange = (value: string): void => {
    setCompany(value);
    setExternalStatus(InputStatus.empty());
  };

  const handleCountryChange = (value: string): void => {
    setCountry(value);
    setExternalStatus(InputStatus.empty());
  };

  const handlePasswordStatusChange = (status: InputStatus): void => {
    setPasswordStatus(status);
  };

  const validateEmail = (value: string): boolean | undefined => {
    let status = getEmailStatus(value, []);

    if (status.isValid) {
      const emailDomain = value.split('@').pop()?.toLowerCase();
      if (emailDomain && config.appConfig.emailDenyList.includes(emailDomain)) {
        status = new InputStatus(false, ['email_domain_not_allowed']);
      }
    }

    setEmailStatus(status);
    return status.isValid;
  };

  const handleRegister = async (): Promise<void> => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = passwordStatus.isValid;
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsRegistrationPending(true);

    await withLoaderAsync(registerUser, 'create-account-button');
  };

  const registerUser = async (): Promise<void> => {
    const currentTimeZoneOffset = currentTimeZone?.value || 0;
    const result = await authService.register({
      email,
      password,
      company,
      country,
      timeZoneOffset: currentTimeZoneOffset,
    });

    if (result instanceof ACDLError) {
      const errorCodes: TranslationKey[] = result.apiErrorResponse ? TranslateServerErrorCodes(result.apiErrorResponse.errorCodes) : ['something_went_wrong'];

      setExternalStatus(new InputStatus(false, errorCodes));
      setIsRegistrationPending(false);
    } else {
      history.push(`/resendConfirmationEmail?email=${encodeURIComponent(email)}`);
    }
  };

  const presetUrlData = (): void => {
    const searchParams = qs.parse(location.search.slice(1)) as { workspaceId: string | undefined };
    if (searchParams.workspaceId !== undefined) {
      workspaceStore.setPreferredWorkspace(searchParams.workspaceId.toLowerCase());
    }

    authService.setRouteReferrer(location.state);
  };

  if (authStore.isAuthenticated) {
    return <Redirect to={authService.getAndClearReferrer()} />;
  }

  return (
    <>
      <RegistrationForm
        onEmailChange={handleEmailChange}
        onPasswordChange={handlePasswordChange}
        onPasswordStatusChange={handlePasswordStatusChange}
        validateEmail={validateEmail}
        onRegister={handleRegister}
        emailStatus={emailStatus}
        passwordStatus={passwordStatus}
        externalStatus={externalStatus}
        isDisabledSubmit={isRegistrationPending}
        email={email}
        onCompanyChange={handleCompanyChange}
        company={company}
        onCountryChange={handleCountryChange}
        country={country}
      />
      <ResolutionWarningCard />
    </>
  );
};
